import React, { useState, useEffect, useMemo } from 'react';
import { getPokemonPool } from '../../data/regulations';
import { TYPE_EFFECTIVENESS } from '../../utils/pokemonTypes';
import TypeBadge from '../TypeBadge';
import { getTypeColor, getTypeTextColor } from '../../data/typeColors';
import { WEATHER_SETTERS, TERRAIN_SETTERS, MOVETYPE_WEATHER } from '../../data/commonSets';
import QuizModal from './QuizModal';
import './BattleQuiz.css';

const COMMON_ITEMS = {
  'Choice Band': 1.5,
  'Choice Specs': 1.5,
  'Life Orb': 1.3,
  'Expert Belt': 1.2,
  'Assault Vest': 1.0,
  'Leftovers': 1.0,
  'Heavy-Duty Boots': 1.0,
  'Air Balloon': 1.0,
  'Focus Sash': 1.0,
  'Rocky Helmet': 1.0,
};

const WEATHER = {
  'Rain': {
    boost: ['Water'],
    reduce: ['Fire'],
    icon: '🌧️',
    description: 'Boosts Water moves, weakens Fire moves'
  },
  'Sun': {
    boost: ['Fire'],
    reduce: ['Water'],
    icon: '☀️',
    description: 'Boosts Fire moves, weakens Water moves'
  },
  'Sand': {
    icon: '🏜️',
    description: 'Boosts Rock Sp. Def — no move power change'
  },
  'Snow': {
    icon: '❄️',
    description: 'Boosts Ice Def — no move power change'
  }
};

const TERRAIN = {
  'Electric': {
    boost: ['Electric'],
    icon: '⚡',
    description: 'Boosts Electric moves for grounded Pokémon'
  },
  'Grassy': {
    boost: ['Grass'],
    icon: '🌱',
    description: 'Boosts Grass moves for grounded Pokémon'
  },
  'Misty': {
    reduce: ['Dragon'],
    icon: '🌸',
    description: 'Weakens Dragon moves for grounded Pokémon'
  },
  'Psychic': {
    boost: ['Psychic'],
    icon: '🔮',
    description: 'Boosts Psychic moves for grounded Pokémon'
  }
};

const MOVE_TYPES = ['Normal', 'Fire', 'Water', 'Electric', 'Grass', 'Ice', 'Fighting', 'Poison', 'Ground', 'Flying', 'Psychic', 'Bug', 'Rock', 'Ghost', 'Dragon', 'Dark', 'Steel', 'Fairy'];


// Abilities that affect the damage multiplier in this quiz.
// role: 'atk' = boosts attacker's damage; 'def' = reduces incoming damage to defender.
// effect codes:
//   atk_phys           — always boosts physical
//   atk_spec           — always boosts special
//   atk_phys_sun       — boosts physical when Sun is active
//   atk_spec_sun       — boosts special when Sun is active
//   atk_phys_eterrain  — boosts physical when Electric Terrain is active
//   atk_spec_eterrain  — boosts special when Electric Terrain is active
//   def_phys           — reduces incoming physical damage
const POKEMON_ABILITIES = {
  'Chien-Pao':    { name: 'Sword of Ruin',   role: 'atk', effect: 'atk_phys',          multiplier: 4/3 },
  'Chi-Yu':       { name: 'Beads of Ruin',    role: 'atk', effect: 'atk_spec',          multiplier: 4/3 },
  'Koraidon':     { name: 'Orichalcum Pulse', role: 'atk', effect: 'atk_phys_sun',      multiplier: 1.3 },
  'Miraidon':     { name: 'Hadron Engine',    role: 'atk', effect: 'atk_spec_eterrain', multiplier: 1.3 },
  'Flutter Mane': { name: 'Protosynthesis',   role: 'atk', effect: 'atk_spec_sun',      multiplier: 1.3 },
  'Walking Wake': { name: 'Protosynthesis',   role: 'atk', effect: 'atk_spec_sun',      multiplier: 1.3 },
  'Roaring Moon': { name: 'Protosynthesis',   role: 'atk', effect: 'atk_phys_sun',      multiplier: 1.3 },
  'Great Tusk':   { name: 'Protosynthesis',   role: 'atk', effect: 'atk_phys_sun',      multiplier: 1.3 },
  'Iron Bundle':  { name: 'Quark Drive',      role: 'atk', effect: 'atk_spec_eterrain', multiplier: 1.3 },
  'Iron Hands':   { name: 'Quark Drive',      role: 'atk', effect: 'atk_phys_eterrain', multiplier: 1.3 },
  'Iron Moth':    { name: 'Quark Drive',      role: 'atk', effect: 'atk_spec_eterrain', multiplier: 1.3 },
  'Incineroar':   { name: 'Intimidate',       role: 'def', effect: 'def_phys',          multiplier: 2/3 },
};

function BattleQuiz({ isOpen, onClose, regulation = 'all' }) {
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [score, setScore] = useState(0);
  const [totalQuestions, setTotalQuestions] = useState(0);
  const [showFeedback, setShowFeedback] = useState(false);
  const [isCorrect, setIsCorrect] = useState(false);
  const [showTypes, setShowTypes] = useState(false);
  const [selectedMultiplier, setSelectedMultiplier] = useState(null);

  const pool = useMemo(() => getPokemonPool(regulation), [regulation]);

  const generateQuestion = () => {
    try {
      // Select two different random Pokemon
      const pokemon1Index = Math.floor(Math.random() * pool.length);
      let pokemon2Index;
      do {
        pokemon2Index = Math.floor(Math.random() * pool.length);
      } while (pokemon2Index === pokemon1Index);

      const attacker = { ...pool[pokemon1Index] };
      const defender = { ...pool[pokemon2Index] };

      // Ensure types arrays exist
      attacker.types = attacker.types || ['Normal'];
      defender.types = defender.types || ['Normal'];

      // Tera is not available in Regulation M-A
      const teraEnabled = regulation !== 'reg-ma';
      const hasTeraAttacker = teraEnabled && Math.random() < 0.2;
      const hasTeraDefender = teraEnabled && Math.random() < 0.1;

      // Randomly assign items from COMMON_ITEMS
      const items = Object.keys(COMMON_ITEMS);
      const attackerItem = items[Math.floor(Math.random() * items.length)];
      const defenderItem = items[Math.floor(Math.random() * items.length)];

      // Generate a move with higher chance of STAB — 85% in real competitive play
      const moveType = Math.random() < 0.85
        ? attacker.types[Math.floor(Math.random() * attacker.types.length)]
        : MOVE_TYPES[Math.floor(Math.random() * MOVE_TYPES.length)];

      // Weather: weather setters almost always have their weather; otherwise the
      // move type hints at a likely weather (Fire → Sun, Water → Rain, Ice → Snow).
      let weatherType = null;
      const preferredWeather = WEATHER_SETTERS[attacker.name];
      if (preferredWeather && Math.random() < 0.80) {
        weatherType = preferredWeather;
      } else if (MOVETYPE_WEATHER[moveType] && Math.random() < 0.40) {
        weatherType = MOVETYPE_WEATHER[moveType];
      } else if (Math.random() < 0.15) {
        const weatherKeys = Object.keys(WEATHER);
        weatherType = weatherKeys[Math.floor(Math.random() * weatherKeys.length)];
      }

      // Terrain: terrain setters almost always have their terrain active.
      let terrainType = null;
      const preferredTerrain = TERRAIN_SETTERS[attacker.name] || TERRAIN_SETTERS[defender.name];
      if (preferredTerrain && Math.random() < 0.75) {
        terrainType = preferredTerrain;
      } else if (Math.random() < 0.15) {
        const terrainKeys = Object.keys(TERRAIN);
        terrainType = terrainKeys[Math.floor(Math.random() * terrainKeys.length)];
      }

      const moveCategory = Math.random() < 0.5 ? 'Physical' : 'Special';
      const teraType = hasTeraAttacker ? MOVE_TYPES[Math.floor(Math.random() * MOVE_TYPES.length)] : null;

      // Pre-calculate the correct multiplier and store components
      let correctMultiplier = 1;
      
      // Calculate type effectiveness
      let typeEffectiveness = 1;
      const defenderFinalTypes = hasTeraDefender ? 
        [MOVE_TYPES[Math.floor(Math.random() * MOVE_TYPES.length)]] : 
        defender.types;
      
      defenderFinalTypes.forEach(defenderType => {
        if (TYPE_EFFECTIVENESS[moveType]?.[defenderType] !== undefined) {
          typeEffectiveness *= TYPE_EFFECTIVENESS[moveType][defenderType];
        }
      });
      correctMultiplier *= typeEffectiveness;

      // Calculate STAB
      const hasOriginalTypeMatch = attacker.types.includes(moveType);
      const hasTeraTypeMatch = teraType === moveType;
      
      let stabMultiplier = 1.0;
      if (hasTeraTypeMatch) {
        if (hasOriginalTypeMatch) {
          stabMultiplier = 2.0;
        } else {
          stabMultiplier = 1.5;
        }
      } else if (hasOriginalTypeMatch) {
        stabMultiplier = 1.5;
      }
      correctMultiplier *= stabMultiplier;

      // Calculate item effect
      let itemEffect = 1.0;
      if (attackerItem === 'Choice Band' && moveCategory === 'Physical') {
        itemEffect = COMMON_ITEMS['Choice Band'];
      } else if (attackerItem === 'Choice Specs' && moveCategory === 'Special') {
        itemEffect = COMMON_ITEMS['Choice Specs'];
      } else if (attackerItem === 'Life Orb') {
        itemEffect = COMMON_ITEMS['Life Orb'];
      } else if (attackerItem === 'Expert Belt' && typeEffectiveness > 1) {
        itemEffect = COMMON_ITEMS['Expert Belt'];
      }
      correctMultiplier *= itemEffect;

      // Apply weather effects
      if (weatherType) {
        if (WEATHER[weatherType].boost?.includes(moveType)) {
          correctMultiplier *= 1.5;
        } else if (WEATHER[weatherType].reduce?.includes(moveType)) {
          correctMultiplier *= 0.5;
        }
      }

      // Apply terrain effects for grounded Pokémon
      if (terrainType) {
        if (TERRAIN[terrainType].boost?.includes(moveType)) {
          correctMultiplier *= 1.3;
        } else if (TERRAIN[terrainType].reduce?.includes(moveType)) {
          correctMultiplier *= 0.5;
        }
      }

      // Apply ability effects
      let attackerAbilityMult = 1.0;
      let defenderAbilityMult = 1.0;
      const atkAbilityData = POKEMON_ABILITIES[attacker.name];
      const defAbilityData = POKEMON_ABILITIES[defender.name];

      if (atkAbilityData && atkAbilityData.role === 'atk') {
        const { effect, multiplier } = atkAbilityData;
        const active = (
          (effect === 'atk_phys'          && moveCategory === 'Physical') ||
          (effect === 'atk_spec'          && moveCategory === 'Special')  ||
          (effect === 'atk_phys_sun'      && weatherType === 'Sun'      && moveCategory === 'Physical') ||
          (effect === 'atk_spec_sun'      && weatherType === 'Sun'      && moveCategory === 'Special')  ||
          (effect === 'atk_phys_eterrain' && terrainType === 'Electric' && moveCategory === 'Physical') ||
          (effect === 'atk_spec_eterrain' && terrainType === 'Electric' && moveCategory === 'Special')
        );
        if (active) {
          attackerAbilityMult = multiplier;
          correctMultiplier *= multiplier;
        }
      }

      if (defAbilityData && defAbilityData.role === 'def') {
        const { effect, multiplier } = defAbilityData;
        if (effect === 'def_phys' && moveCategory === 'Physical') {
          defenderAbilityMult = multiplier;
          correctMultiplier *= multiplier;
        }
      }

      // Round to avoid floating-point display artifacts (e.g. 3.9000000004)
      correctMultiplier = Math.round(correctMultiplier * 100) / 100;

      const weatherEffect = (weatherType && (
        WEATHER[weatherType].boost?.includes(moveType) ? 1.5 :
        WEATHER[weatherType].reduce?.includes(moveType) ? 0.5 : 1.0
      )) || 1.0;
      const terrainEffect = (terrainType && (
        TERRAIN[terrainType].boost?.includes(moveType) ? 1.3 :
        TERRAIN[terrainType].reduce?.includes(moveType) ? 0.5 : 1.0
      )) || 1.0;

      const calculationDetails = {
        typeEffectiveness,
        stab: stabMultiplier,
        itemEffect,
        weatherEffect,
        terrainEffect,
        attackerAbility: attackerAbilityMult !== 1.0
          ? { name: atkAbilityData.name, multiplier: Math.round(attackerAbilityMult * 100) / 100 }
          : null,
        defenderAbility: defenderAbilityMult !== 1.0
          ? { name: defAbilityData.name, multiplier: Math.round(defenderAbilityMult * 100) / 100 }
          : null,
        finalMultiplier: correctMultiplier
      };

      const newQuestion = {
        attacker: {
          ...attacker,
          item: attackerItem,
          teraType
        },
        defender: {
          ...defender,
          item: defenderItem,
          teraType: hasTeraDefender ? defenderFinalTypes[0] : null
        },
        move: {
          type: moveType,
          category: moveCategory
        },
        weather: weatherType,
        terrain: terrainType,
        correctMultiplier,
        calculationDetails
      };

      setCurrentQuestion(newQuestion);
      setShowFeedback(false);
      setSelectedMultiplier(null);
    } catch (error) {
      console.error('Error generating question:', error);
    }
  };

  const checkAnswer = (multiplier) => {
    if (!currentQuestion || showFeedback) return;

    const isAnswerCorrect = Math.abs(multiplier - currentQuestion.correctMultiplier) < 0.01;
    setIsCorrect(isAnswerCorrect);
    setScore(prev => isAnswerCorrect ? prev + 1 : prev);
    setTotalQuestions(prev => prev + 1);
    setShowFeedback(true);
    setSelectedMultiplier(multiplier);
  };

  useEffect(() => {
    if (isOpen) {
      setScore(0);
      setTotalQuestions(0);
      setShowTypes(false);
      generateQuestion();
    }
  }, [isOpen]);

  return (
    <QuizModal isOpen={isOpen} onClose={onClose}>
      <div className="battle-quiz-container">
        <div className="quiz-header">
          <h3>Battle Scenario Quiz</h3>
          <div className="quiz-controls">
            <div className="score">Score: {score}/{totalQuestions}</div>
            <label className="show-types-toggle">
              <input
                type="checkbox"
                checked={showTypes}
                onChange={(e) => setShowTypes(e.target.checked)}
              />
              Show Types
            </label>
            {showFeedback && (
              <button className="next-question-arrow" onClick={generateQuestion}>
                <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
                  <path d="M12 4l-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8z"/>
                </svg>
              </button>
            )}
            <button className="close-button" onClick={onClose}>×</button>
          </div>
        </div>

        {currentQuestion && (
          <div className="battle-scenario">
            <div className="pokemon-battle">
              <div className="attacker pokemon-card"
                style={{ borderLeft: `4px solid ${getTypeColor(currentQuestion.attacker.types?.[0])}` }}>
                <img
                  src={`https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon/${currentQuestion.attacker.spriteId ?? currentQuestion.attacker.id}.png`}
                  alt={currentQuestion.attacker.name}
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = 'https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon/0.png';
                  }}
                />
                <h3>{currentQuestion.attacker.name}</h3>
                <div className="type-badges">
                  {showTypes && currentQuestion.attacker.types.map((type, index) => (
                    <TypeBadge key={index} type={type} />
                  ))}
                  {currentQuestion.attacker.teraType && (
                    <span className="type-badge tera" style={{ backgroundColor: getTypeColor(currentQuestion.attacker.teraType) }}>
                      Tera {currentQuestion.attacker.teraType}
                    </span>
                  )}
                </div>
                <div className="pokemon-details">
                  {POKEMON_ABILITIES[currentQuestion.attacker.name] && (
                    <div className="stat-row">
                      <span className="stat-label">Ability</span>
                      <span className="stat-value">{POKEMON_ABILITIES[currentQuestion.attacker.name].name}</span>
                    </div>
                  )}
                  <div className="stat-row">
                    <span className="stat-label">Item</span>
                    <span className="stat-value">{currentQuestion.attacker.item}</span>
                  </div>
                </div>
              </div>

              <div className="battle-info">
                <div className="move-info">
                  <div className="type-badge" style={{ backgroundColor: getTypeColor(currentQuestion.move.type), color: getTypeTextColor(currentQuestion.move.type) }}>
                    {currentQuestion.move.type} Move
                  </div>
                  <p className="move-category">{currentQuestion.move.category}</p>
                </div>
                <div className="active-effects">
                  {currentQuestion.weather && (
                    <span className="effect-chip weather">
                      {WEATHER[currentQuestion.weather].icon} {currentQuestion.weather}
                    </span>
                  )}
                  {currentQuestion.terrain && (
                    <span className="effect-chip terrain">
                      {TERRAIN[currentQuestion.terrain].icon} {currentQuestion.terrain} Terrain
                    </span>
                  )}
                </div>
              </div>

              <div className="defender pokemon-card"
                style={{ borderLeft: `4px solid ${getTypeColor(currentQuestion.defender.types?.[0])}` }}>
                <img
                  src={`https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon/${currentQuestion.defender.spriteId ?? currentQuestion.defender.id}.png`}
                  alt={currentQuestion.defender.name}
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = 'https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon/0.png';
                  }}
                />
                <h3>{currentQuestion.defender.name}</h3>
                <div className="type-badges">
                  {showTypes && currentQuestion.defender.types.map((type, index) => (
                    <TypeBadge key={index} type={type} />
                  ))}
                  {currentQuestion.defender.teraType && (
                    <span className="type-badge tera" style={{ backgroundColor: getTypeColor(currentQuestion.defender.teraType) }}>
                      Tera {currentQuestion.defender.teraType}
                    </span>
                  )}
                </div>
                <div className="pokemon-details">
                  {POKEMON_ABILITIES[currentQuestion.defender.name] && (
                    <div className="stat-row">
                      <span className="stat-label">Ability</span>
                      <span className="stat-value">{POKEMON_ABILITIES[currentQuestion.defender.name].name}</span>
                    </div>
                  )}
                  <div className="stat-row">
                    <span className="stat-label">Item</span>
                    <span className="stat-value">{currentQuestion.defender.item}</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="multiplier-selection">
              <p>What is the total damage multiplier?</p>
              <div className="multiplier-buttons">
                {generateMultiplierOptions(currentQuestion.correctMultiplier).map((multiplier) => (
                  <button
                    key={multiplier}
                    className={`multiplier-button ${
                      selectedMultiplier === multiplier ? 
                        (isCorrect ? 'correct' : 'incorrect') : ''
                    }`}
                    onClick={() => checkAnswer(multiplier)}
                    disabled={showFeedback}
                  >
                    ×{multiplier}
                  </button>
                ))}
              </div>
            </div>

            {showFeedback && currentQuestion.calculationDetails && (
              <>
                <div className={`feedback ${isCorrect ? 'correct' : 'incorrect'}`}>
                  <p>{isCorrect ? '✓ Correct!' : '✗ Incorrect'}</p>
                  <div className="multiplier-breakdown">
                    <h4>Damage Multiplier Breakdown</h4>
                    <div className="breakdown-details">
                      <p>
                        <strong>Type Effectiveness</strong>
                        <span>
                          ×{currentQuestion.calculationDetails.typeEffectiveness.toFixed(2)}
                          {currentQuestion.calculationDetails.typeEffectiveness === 0 && " — no effect"}
                          {currentQuestion.calculationDetails.typeEffectiveness > 1 && " — super effective"}
                          {currentQuestion.calculationDetails.typeEffectiveness < 1 && currentQuestion.calculationDetails.typeEffectiveness > 0 && " — not very effective"}
                        </span>
                      </p>
                      <p>
                        <strong>STAB</strong>
                        <span>
                          ×{currentQuestion.calculationDetails.stab.toFixed(2)}
                          {currentQuestion.calculationDetails.stab === 2.0 && " — Tera matches original"}
                          {currentQuestion.calculationDetails.stab === 1.5 && " — same type"}
                          {currentQuestion.calculationDetails.stab === 1.0 && " — none"}
                        </span>
                      </p>
                      <p>
                        <strong>Item</strong>
                        <span>
                          ×{currentQuestion.calculationDetails.itemEffect.toFixed(2)}
                          {currentQuestion.calculationDetails.itemEffect !== 1 ? ` — ${currentQuestion.attacker.item}` : " — no boost"}
                        </span>
                      </p>
                      {currentQuestion.weather && (
                        <p>
                          <strong>Weather ({currentQuestion.weather})</strong>
                          <span>×{currentQuestion.calculationDetails.weatherEffect.toFixed(2)}</span>
                        </p>
                      )}
                      {currentQuestion.terrain && (
                        <p>
                          <strong>Terrain ({currentQuestion.terrain})</strong>
                          <span>×{currentQuestion.calculationDetails.terrainEffect.toFixed(2)}</span>
                        </p>
                      )}
                      {currentQuestion.calculationDetails.attackerAbility && (
                        <p>
                          <strong>Ability ({currentQuestion.calculationDetails.attackerAbility.name})</strong>
                          <span>×{currentQuestion.calculationDetails.attackerAbility.multiplier.toFixed(2)}</span>
                        </p>
                      )}
                      {currentQuestion.calculationDetails.defenderAbility && (
                        <p>
                          <strong>Ability ({currentQuestion.calculationDetails.defenderAbility.name})</strong>
                          <span>×{currentQuestion.calculationDetails.defenderAbility.multiplier.toFixed(2)}</span>
                        </p>
                      )}
                      <p className="final-multiplier">
                        <strong>Final Multiplier</strong>
                        <span>×{currentQuestion.calculationDetails.finalMultiplier}</span>
                      </p>
                    </div>
                  </div>
                </div>
                <button className="next-question-btn" onClick={generateQuestion}>
                  Next Question
                  <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
                    <path d="M12 4l-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8z"/>
                  </svg>
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </QuizModal>
  );
}


function generateMultiplierOptions(correctMultiplier) {
  // Pool of plausible competitive multipliers (all rounded to 2 dp)
  const pool = [0, 0.25, 0.5, 0.67, 1, 1.33, 1.5, 2, 3, 4];

  // Pick the 4 closest pool values that aren't the correct answer
  const distractors = pool
    .filter(v => Math.abs(v - correctMultiplier) > 0.01)
    .sort((a, b) => Math.abs(a - correctMultiplier) - Math.abs(b - correctMultiplier))
    .slice(0, 4);

  return [correctMultiplier, ...distractors].sort((a, b) => a - b);
}

export default BattleQuiz; 